import { AlertTriangle, CircleGauge, Clock3 } from 'lucide-react';
import type { TradingPortfolioHeatResponse } from 'shared';

import { formatCurrency } from '../../lib/format/currency';
import { Card } from '../ui/Card';

interface TradingPortfolioHeatCardProps {
  heat: TradingPortfolioHeatResponse | undefined;
  isError: boolean;
  isLoading: boolean;
}

function formatPct(value: string | null): string {
  if (value === null) return 'Unavailable';
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(Number(value))}%`;
}

function issueSummary(heat: TradingPortfolioHeatResponse): string[] {
  const issues: string[] = [];
  if (heat.counts.missingStopCount > 0) {
    issues.push(
      `${heat.counts.missingStopCount} ${heat.counts.missingStopCount === 1 ? 'position is' : 'positions are'} missing a stop`,
    );
  }
  if (heat.counts.unreliableStopCount > 0) {
    issues.push(
      `${heat.counts.unreliableStopCount} ${heat.counts.unreliableStopCount === 1 ? 'position has' : 'positions have'} unreliable stop history`,
    );
  }
  if (heat.counts.currencyMismatchCount > 0) {
    issues.push(
      `${heat.counts.currencyMismatchCount} ${heat.counts.currencyMismatchCount === 1 ? 'position has' : 'positions have'} an incompatible currency`,
    );
  }
  if (heat.counts.historyIncompleteCount > 0) {
    issues.push(
      `${heat.counts.historyIncompleteCount} unresolved ${heat.counts.historyIncompleteCount === 1 ? 'position history' : 'position histories'}`,
    );
  }
  return issues;
}

export function TradingPortfolioHeatCard({
  heat,
  isError,
  isLoading,
}: TradingPortfolioHeatCardProps) {
  return (
    <Card
      aria-label="Trading portfolio heat"
      header={
        <div className="flex items-start gap-3">
          <CircleGauge aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">
              {heat?.status === 'INCOMPLETE' ? 'Portfolio Heat: Incomplete' : 'Portfolio Heat'}
            </h2>
            <p className="text-sm text-text-muted">Current entry-to-stop capital risk.</p>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading portfolio heat...</p>
      ) : isError || !heat ? (
        <p className="text-sm text-danger">Could not load portfolio heat.</p>
      ) : (
        <div className="space-y-4">
          <dl className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="min-w-0">
              <dt className="text-sm text-text-muted">Known risk</dt>
              <dd className="mt-1 break-words text-2xl font-semibold tabular-nums">
                {formatCurrency(heat.knownRiskAmount, heat.account.currency)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm text-text-muted">Known heat</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums">
                {formatPct(heat.knownHeatPctNav)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm text-text-muted">Risk budget</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums">
                {heat.budget.configured ? formatPct(heat.budget.pctNav) : 'Not configured'}
              </dd>
              {heat.budget.amount !== null ? (
                <dd className="text-sm tabular-nums text-text-muted">
                  {formatCurrency(heat.budget.amount, heat.account.currency)}
                </dd>
              ) : null}
            </div>
            <div className="min-w-0">
              <dt className="text-sm text-text-muted">Remaining budget</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums">
                {formatPct(heat.remainingBudget.pctNav)}
              </dd>
              {heat.remainingBudget.amount !== null ? (
                <dd className="text-sm tabular-nums text-text-muted">
                  {formatCurrency(heat.remainingBudget.amount, heat.account.currency)}
                </dd>
              ) : null}
            </div>
          </dl>

          {heat.status === 'INCOMPLETE' ? (
            <div className="flex items-start gap-2 border-t border-border pt-4 text-sm text-warn">
              <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p>Remaining budget unavailable while portfolio heat is incomplete.</p>
                {issueSummary(heat).length > 0 ? (
                  <p className="mt-1 text-text-muted">{issueSummary(heat).join('; ')}.</p>
                ) : null}
              </div>
            </div>
          ) : null}

          {!heat.nav.usable ? (
            <p className="border-t border-border pt-4 text-sm text-warn">
              Account NAV is unavailable. Absolute known risk remains available.
            </p>
          ) : null}
          {heat.nav.stale ? (
            <p className="flex items-center gap-2 border-t border-border pt-4 text-sm text-warn">
              <Clock3 aria-hidden="true" className="h-4 w-4 shrink-0" />
              NAV snapshot is more than 24 hours old.
            </p>
          ) : null}
          {heat.knownRiskExceedsBudgetByAmount !== null ? (
            <p className="border-t border-border pt-4 text-sm font-medium text-danger">
              Known risk already exceeds the risk budget by{' '}
              {formatCurrency(heat.knownRiskExceedsBudgetByAmount, heat.account.currency)}.
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}
